import { useRef } from "react";
import type { ScanFormat } from "./scan";
import { classifyScan, type ScanPayload } from "./payload";
import {
  useScanHistoryStore,
  type ScanOutcome,
} from "../../shared/store/scanHistory";

/**
 * One scan's history entry, remembered while its resolution or a follow-up
 * write is in flight. The handle captures its own entry id, so an async
 * resolution can never write its outcome onto a newer scan.
 */
export interface ScanHistoryEntryHandle {
  /** The stored entry id, or undefined when signed out or the code is empty. */
  readonly id?: string;
  /** The canonical code stored for this scan (label UUID or trimmed raw). */
  readonly code: string;
  /**
   * The classified payload the preset resolves from, so a caller never
   * re-classifies the code it just recorded (scan-resolution 03).
   */
  readonly payload: ScanPayload;
  /** Write this scan's outcome snapshot. */
  outcome: (outcome: ScanOutcome) => void;
}

/**
 * The shared record-before/outcome-after seam for every scan preset
 * (results-04, scan-resolution 01): record the decode when it happens, attach
 * the display snapshot once resolution lands, and keep the entry reachable for
 * a later create step. Keeping it in one hook stops the four presets from
 * drifting apart, and it is the only place that writes scan history.
 */
export function useScanHistoryEntry() {
  const current = useRef<ScanHistoryEntryHandle | null>(null);

  const makeHandle = (id: string | undefined, payload: ScanPayload): ScanHistoryEntryHandle => ({
    id,
    code: canonicalCodeOf(payload),
    payload,
    outcome: (outcome) => {
      // The handle's captured id is the outcome seam: an async resolution can
      // never update a newer scan's entry.
      if (id) useScanHistoryStore.getState().updateOutcome(id, outcome);
    },
  });

  /**
   * Record a decode or typed submission and remember its entry.
   *
   * @param text the decoded or typed code.
   * @param format the symbology, or `manual` for typed entry.
   */
  const record = (text: string, format: ScanFormat): ScanHistoryEntryHandle => {
    // Classify once: the stored entry, the handle's canonical code and the
    // payload the preset resolves from all come from the same classification.
    const payload = classifyScan(text, format);
    const code = canonicalCodeOf(payload);
    const id = code
      ? useScanHistoryStore.getState().recordScan({ code, format, payload })
      : undefined;
    const handle = makeHandle(id, payload);
    current.current = handle;
    return handle;
  };

  /**
   * Remember an existing entry instead of appending (a `Load` from the
   * history overlay refreshes that entry's outcome without reordering).
   */
  const adopt = (entryId: string | undefined, payload: ScanPayload): ScanHistoryEntryHandle => {
    const handle = makeHandle(entryId, payload);
    current.current = handle;
    return handle;
  };

  /** Write the current entry's outcome, when there is one. */
  const outcome = (snapshot: ScanOutcome): void => {
    current.current?.outcome(snapshot);
  };

  return { record, adopt, outcome, current };
}

/**
 * The code a scan is stored and resolved under: a label's canonical lowercase
 * UUID, or the trimmed raw text of every other payload.
 *
 * @param payload the classified scan.
 * @returns the canonical code (empty for whitespace-only text).
 */
function canonicalCodeOf(payload: ScanPayload): string {
  return payload.kind === "label" ? payload.uuid : payload.raw.trim();
}
